// Radiators.co.uk predictive search (2026-10-14). One small module, no imports, loaded with `defer` semantics.
//
// Progressive enhancement of every `form[data-rad-search]` (snippets/rad-search-form.liquid). Without JavaScript the
// form is a plain GET to /search and works. With it:
//   * the query is normalised exactly like search/normaliser.py theme_query (Gate B 6.3 plus the L-22 tag syntax; parity is tested by
//     tests/theme/test_search_normaliser_parity.py), so "600 x 1200 mm", "10,000 btu" or "k-2" reach the importer's
//     search tags;
//   * predictive results come from the Section Rendering API (/search/suggest?section_id=rad-predictive-search):
//     products on the normalised query, collections and suggestions on the raw one (two calls only when they differ);
//   * the panel is an absolutely positioned overlay, so opening it never moves the page (no layout shift);
//   * keyboard: ArrowDown moves from the field into the results, ArrowUp/ArrowDown move between them, Escape closes
//     and returns focus to the field; a polite live region announces the result count.
// No personal data is sent anywhere; the query goes only to the shop's own search endpoint.

const NUM_WORDS = /\p{Nd}/u;

/** Non-ASCII decimal digits to ASCII (search/normaliser.py ascii_digits). */
function asciiDigits(s) {
  let out = '';
  for (const ch of s) {
    const cp = ch.codePointAt(0);
    if (cp > 127 && NUM_WORDS.test(ch)) {
      let zero = cp;
      while (cp - zero < 9 && NUM_WORDS.test(String.fromCodePoint(zero - 1))) zero -= 1;
      out += String(cp - zero);
    } else {
      out += ch;
    }
  }
  return out;
}

const W = '[\\p{L}\\p{N}_]'; // Python's Unicode \w (str.isalnum() or "_"), so \b matches normaliser.py
const B0 = `(?<!${W})`; // \b before a word character
const B1 = `(?!${W})`; // \b after a word character
const re = (src, flags = 'gu') => new RegExp(src.replaceAll('\\b<', B0).replaceAll('\\b>', B1), flags);

/** The Gate B query normaliser (search/normaliser.py normalise_query with dims=True). */
export function normaliseQuery(q) {
  let s = asciiDigits(String(q || '').normalize('NFKC')).toLowerCase();
  s = s.replace(/(\d),(\d{3})/g, '$1$2');
  s = s.replace(re('(\\d)\\s*btus?\\b>'), '$1 btu');
  s = s.replace(re('\\b<k-(\\d)\\b>'), 'k$1');
  s = s.replace(re('\\b<type-(\\d\\d)\\b>'), 'type $1');
  s = s.replace(re('\\b<p-plus\\b>'), 'p plus');
  s = s.replace(re('\\b<p\\+'), 'p plus');
  s = s.replace(re('\\b<radaitor\\b>'), 'radiator');
  s = s.replace(re('\\b<(\\d{3,4})(?:\\s*mm)?\\s*(?:x|\\*|by|-)\\s*(\\d{3,4})(?:\\s*mm)?\\b>'), '$1x$2');
  s = s.replace(re('\\b<(\\d{3,4})\\s*mm\\s+(wide|long)\\b>'), 'w$1');
  s = s.replace(re('\\b<(wide|long)\\s+(\\d{3,4})\\s*mm\\b>'), 'w$2');
  s = s.replace(re('\\b<(\\d{3,4})\\s*mm\\s+(high|tall)\\b>'), 'h$1');
  s = s.replace(re('\\b<(high|tall)\\s+(\\d{3,4})\\s*mm\\b>'), 'h$2');
  s = s.replace(re('\\b<(\\d{3,6})\\s+btu\\b>'), (m, n) => (Number(n) % 250 === 0 ? `b${Number(n)} btu` : m));
  s = s.replace(re('\\b<(\\d{2,5})\\s*(?:watts?|w)\\b>'), '$1w');
  return s.replace(/\s+/gu, ' ').trim();
}

const NOT_CODE = /^(?:[bwhkt]\d+|\d+w|\d+x\d+|\d+mm|[a-z]\d{1,2})$/u;

/**
 * Shopify tag field syntax (search/normaliser.py tag_syntax, L-22 live finding 2026-10-15): the public search does not
 * word-match `prefix:value` tags, but `tag:"btu:b12000"` matches and space-separated clauses are ANDed.
 */
export function tagSyntax(s) {
  if (!s.includes(' ') && s.length >= 5 && /\d/u.test(s) && !NOT_CODE.test(s)) {
    if (/^\d+$/u.test(s)) return `${s} OR tag:"sku:${s}" OR tag:"mpn:${s}"`;
    if (/^[a-z0-9]+$/u.test(s) && s.length >= 6) return `tag:"sku:${s}" OR tag:"mpn:${s}"`;
    return s;
  }
  let out = s.replace(re('\\b<b(\\d{3,6}) btu\\b>'), 'tag:"btu:b$1"');
  out = out.replace(re('\\b<([wh])(\\d{3,4})\\b>'), 'tag:"size:$1$2"');
  out = out.replace(re('\\b<(\\d{2,5})w\\b>'), 'tag:"watts:$1w"');
  if (out.includes('tag:')) out = out.replace(re('\\b<(vertical|horizontal)\\b>'), 'tag:"orientation:$1"');
  return out;
}

// BEGIN RAD_QUERY_VOCAB (generated by search/build_query_vocab.py; do not edit)
const VOCAB = {"brands":["baxi","biasi","centerbrand","centerrad","centerrail","danfoss","drayton","fernox","honeywell","infrared group","intatec","main","myson","nabis","pegler yorkshire","potterton","purmo","reina","siemens","smiths heaters","stelrad","the radiator company","thorn","tradefix","wallace and squires","west radiators","zehnder"],"ranges":["baxi accessories","biasi chiara","biasi dolomite","center cb md002-ms","centerbrand accessories","centerrad accessories","centerrad column","centerrad compact","centerrad deco","centerrad plan","centerrad streyt","centerrail accessories","centerrail f5i-cr","centerrail md001-ms","danfoss accessories","drayton accessories","drayton valves","fernox f","honeywell accessories","infrared accessories","infrared classic","infrared frameless panel","infrared nrgpro","infrared nrgpro custom","infrared solstice mirror","intatec accessories","main accessories","myson accessories","myson angara","myson avonmore","myson awe","myson bani","myson buttermere","myson column","myson column bench","myson compact","myson decor","myson dee","myson eco450","myson fatala","myson ferlo","myson finesse","myson hi-line convector","myson inn","myson innoko","myson interlude","myson ivector","myson kasai","myson kickspace","myson lindi","myson lo-line convector","myson lst","myson lst super","myson lst super plus","myson maronoa","myson mersey","myson opus","myson ouse","myson plan","myson plan compact","myson plan compact plus","myson plan vertical towel bar","myson premier","myson premier compact","myson premier he","myson premier he imperial","myson premier he metric","myson premier metric","myson rio compact","myson rio linea","myson rio plan","myson saxby","myson select compact","myson sokoto","myson t21","myson t6 compact","myson t6 plan","myson thirlmere","myson ullswater","myson ulow","myson underfloor","myson vertical","myson vertical t20","myson vertical t21","nabis anna","nabis bess","nabis imogen","nabis juliet","nabis karly","nabis katherine","nabis lois","nabis orbis","nabis tilly","pegler yorkshire accessories","potterton accessories","purmo adagio","purmo compla","purmo flores","purmo forza","purmo ligna","purmo lst","purmo paros","purmo plaza","purmo radiator lst","purmo slieve","purmo tinos","reina accessories","reina alento","reina aletta","reina aliano","reina ancora","reina arbori","reina arden","reina arnage","reina bonera","reina breno","reina bronte","reina capelli","reina carpi","reina casina","reina cavo","reina celico","reina chisa","reina claro","reina colona","reina coneva","reina crova","reina dimaro","reina element","reina element touch","reina elisa","reina elvina","reina elvo","reina entice","reina eos","reina fiore","reina flat","reina florina","reina grace","reina helin","reina lago","reina line","reina loge","reina lovere","reina luna","reina marco","reina matera","reina mina","reina misa","reina modal","reina nardo","reina neva","reina nevah","reina nola","reina orne","reina osimo","reina portland","reina rance","reina reflect","reina rezzo","reina ricadi","reina rione","reina serena","reina serpe","reina sesia","reina slimline","reina square","reina surrey","reina teano","reina trento","reina troisi","reina vasto","reina veroli","siemens accessories","smiths accessories","smiths caspian","smiths ecovector","smiths space saver","smiths sterling","smiths sureline","stelrad accessories","stelrad agato","stelrad alu","stelrad alyne","stelrad caliente","stelrad caliente towel rail","stelrad cast iron","stelrad classic column","stelrad cloud","stelrad cobalto","stelrad column","stelrad como","stelrad compact","stelrad compact with style","stelrad concord","stelrad concord lo-line","stelrad concord plane","stelrad concord towel rail","stelrad elegance","stelrad elite","stelrad ellipse","stelrad excel","stelrad fantasia","stelrad flat panel","stelrad green compact","stelrad iridio","stelrad ladder rail","stelrad lecco","stelrad lo-line","stelrad lst","stelrad lst plus vertical","stelrad lst standard","stelrad lsti","stelrad mini rail","stelrad planar","stelrad radical","stelrad silhouette concept","stelrad slimline","stelrad softline","stelrad style","stelrad swing","stelrad towel","stelrad towel rail","stelrad traditional","stelrad ultra coloured towel bar","stelrad vertex","stelrad vistaline","stelrad vita","stelrad vita compact","stelrad vita concord","stelrad vita concord plane","stelrad vita deco","stelrad vita deco concept","stelrad vita eco","stelrad vita plan","stelrad vita silhouette","stelrad vita slimline","stelrad vita ultra","stelrad vita ultra towel bar","sterlad lst","the radiator company athena","the radiator company ellipsis","the radiator company griffin","the radiator company immagina","the radiator company oscar","the radiator company piano","the radiator company piano lato","the radiator company poll","the radiator company pulsar","the radiator company vip","the radiator company vox","thorn accessories","tradefix f5i-tf","wallace and squires accessories","wallace and squires aldgate","wallace and squires brompton","wallace and squires clasico","wallace and squires finchley","wallace and squires finsbury","wallace and squires fitzrovia","wallace and squires fulham","wallace and squires hampstead","wallace and squires highgate","wallace and squires holborn","wallace and squires ionic","wallace and squires new primrose","wallace and squires new studio","wallace and squires oval","wallace and squires sterling","wallace and squires stratford","wallace and squires temple","west abbey","west accessories","west admiral","west amberley","west apex","west bentley","west bloc","west bradley","west commodore","west cube","west delta","west elegance","west ellipse","west eton","west evolve","west faringdon","west gothic","west jaguar","west milan","west poppy","west realm","west rosa","west wave","zehnder accessories","zehnder alban","zehnder alura","zehnder archibald","zehnder arteplano","zehnder aura","zehnder ax","zehnder ax spa","zehnder balmoral","zehnder blitz super","zehnder blok","zehnder blok towel","zehnder buckingham","zehnder charleston","zehnder charleston bar","zehnder chime","zehnder connect","zehnder cove","zehnder deline","zehnder deseo","zehnder excelsior","zehnder fare tech","zehnder flow form","zehnder folio","zehnder forma spa asymmetric","zehnder garda","zehnder klaro","zehnder kleo","zehnder lissett","zehnder lissett towel","zehnder metropolitan","zehnder metropolitan spa","zehnder muralis","zehnder nobis","zehnder olga","zehnder orbit","zehnder osbourne","zehnder ovida","zehnder pera","zehnder quaro spa","zehnder ribbon","zehnder roda","zehnder sfera bow","zehnder splash","zehnder stana","zehnder stellar","zehnder stellar spa","zehnder subway","zehnder svelte","zehnder vision","zehnder vitalo bar","zehnder yucca asymmetric","zehnder zenia","zehnder zeta"],"generic":["accessories","aluminium","and","anthracite","bar","bench","black","cast","chrome","classic","column","compact","convector","cube","custom","designer","double","eco","electric","element","flat","frameless","green","hi-line","horizontal","iron","ladder","line","lo-line","mini","mirror","oval","panel","plan","plus","rad","radiator","radiators","rads","rail","rails","single","slimline","spa","square","standard","style","the","touch","towel","traditional","triple","underfloor","valves","vertical","white","with"],"filler":["radiator","radiators","rad","rads"]};
// END RAD_QUERY_VOCAB

const PANEL_TYPES = { 10: 'p1', 11: 'k1', 20: 'p2', 21: 'p plus', 22: 'k2', 33: 'k3' };

/** Optimal string alignment distance of at most 1 (search/normaliser.py _osa1). */
function osa1(a, b) {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  let i = 0;
  while (i < Math.min(la, lb) && a[i] === b[i]) i += 1;
  if (la === lb) {
    if (a.slice(i + 1) === b.slice(i + 1)) return true;
    return i + 1 < la && a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2);
  }
  if (la > lb) return a.slice(i + 1) === b.slice(i);
  return a.slice(i) === b.slice(i + 1);
}

const tokEq = (q, v) => q === v || (q.length >= 5 && v.length >= 5 && osa1(q, v));
const isClause = (t) => t.startsWith('tag:') || t.startsWith('vendor:');
const cmpStr = (x, y) => (x < y ? -1 : x > y ? 1 : 0);

let CANDIDATES = null;
/** (phrase tokens, clause) pairs, longest first (search/normaliser.py _candidates). */
function candidates() {
  if (CANDIDATES) return CANDIDATES;
  const generic = new Set(VOCAB.generic);
  const brandToks = VOCAB.brands.map((b) => b.split(' '));
  const same = (x, y) => x.length === y.length && x.every((t, k) => t === y[k]);
  const split = VOCAB.ranges.map((r) => {
    const rt = r.split(' ');
    let pre = [];
    for (const bt of brandToks) if (same(rt.slice(0, bt.length), bt) && bt.length > pre.length) pre = bt;
    if (!pre.length && brandToks.some((bt) => bt[0] === rt[0])) pre = rt.slice(0, 1);
    return { rt, pre, model: pre.length ? rt.slice(pre.length) : [] };
  });
  const out = new Map();
  for (const { rt } of split) out.set(rt.join('\u0001'), [rt, rt.join(' ')]);
  for (const { pre, model } of split) {
    for (let n = 1; n <= model.length; n += 1) {
      const m = model.slice(0, n);
      const key = m.join('\u0001');
      if (m.every((t) => generic.has(t)) || out.has(key)) continue;
      const owners = new Set(split.filter((x) => same(x.model.slice(0, n), m)).map((x) => x.pre.join('\u0001')));
      if (owners.size === 1) out.set(key, [m, [...pre, ...m].join(' ')]);
    }
  }
  CANDIDATES = [...out.values()].sort((x, y) => y[0].length - x[0].length || cmpStr(x[1], y[1])
    || cmpStr(x[0].join('\u0001'), y[0].join('\u0001')));
  return CANDIDATES;
}

const MODEL_MAX_WORDS = 5;

/** Words, with a whole quoted tag:"..." or vendor:"..." clause kept as one token (search/normaliser.py _tokens). */
const tokens = (s) => s.match(/(?:tag|vendor):"[^"]*"|\S+/gu) || [];

/** [start, length, clause] of the range phrase (search/normaliser.py _find_range): exact words first; a typo match only
 * when longer than the exact one and unambiguous. */
function findRange(toks, free) {
  const scan = (eq) => {
    let best = null;
    for (const [phrase, clause] of candidates()) {
      const n = phrase.length;
      if (best && n < best[1]) break;
      for (let i = 0; i + n <= toks.length; i += 1) {
        let ok = true;
        for (let k = 0; k < n && ok; k += 1) ok = free[i + k] && eq(toks[i + k], phrase[k]);
        if (ok) {
          if (best === null) best = [i, n, clause];
          else if (best[2] !== clause) return [best, false];
          break;
        }
      }
    }
    return [best, true];
  };
  const [exact] = scan((a, b) => a === b);
  const [fuzzy, unambiguous] = scan(tokEq);
  if (fuzzy && unambiguous && (exact === null || fuzzy[1] > exact[1])) return fuzzy;
  return exact;
}

/**
 * Round 3 clauses (search/normaliser.py model_syntax): panel types ("type 22" to "k2"), a height-by-width pair to its
 * `size:` tag, a range name (exact words first, else one
 * letter off on words of 5 or more when unambiguous) to `tag:"range:..."`, a brand alone to `vendor:"..."`. Code queries and queries of
 * more than 5 words (product names) are left alone.
 */
export function modelSyntax(s) {
  if (!s || s.includes(' OR ') || tokens(s).length > MODEL_MAX_WORDS) return s;
  let toks = tokens(s);
  let free = toks.map((t) => !isClause(t));
  const out = [];
  for (let i = 0; i < toks.length; ) {
    const t = toks[i];
    if (free[i] && t === 'type' && i + 1 < toks.length && free[i + 1] && Object.hasOwn(PANEL_TYPES, toks[i + 1])) {
      out.push(PANEL_TYPES[toks[i + 1]]);
      i += 2;
      continue;
    }
    let m = free[i] ? /^v(\d\d)$/u.exec(t) : null;
    if (m && Object.hasOwn(PANEL_TYPES, m[1])) {
      out.push(PANEL_TYPES[m[1]]);
      i += 1;
      continue;
    }
    m = free[i] ? /^(\d{3,4})x(\d{3,4})$/u.exec(t) : null;
    if (m) {
      out.push(`tag:"size:${m[1]}x${m[2]}"`);
      i += 1;
      continue;
    }
    out.push(t);
    i += 1;
  }
  toks = tokens(out.join(' '));
  free = toks.map((t) => !isClause(t));
  const filler = new Set(VOCAB.filler);
  const hit = findRange(toks, free);
  if (hit) {
    const [i, n, clause] = hit;
    const rest = [...toks.slice(0, i), ...toks.slice(i + n)].filter((t) => !filler.has(t));
    return [`tag:"range:${clause}"`, ...rest].join(' ');
  }
  const wordsAll = toks.filter((t, k) => free[k]);
  for (const words of [wordsAll, wordsAll.filter((t) => !filler.has(t))]) {
    if (!words.length) continue;
    for (const b of VOCAB.brands) {
      const bt = b.split(' ');
      if (words.length === bt.length && words.every((w, k) => tokEq(w, bt[k]))) {
        return [...toks.filter((t, k) => !free[k]), `vendor:"${b}"`].join(' ');
      }
    }
  }
  return toks.join(' ');
}

// Radiators first (2026-11-02): search/normaliser.py radiators_first, the reference.
export const RAD_PARTS_SUFFIX = ' NOT product_type:Accessory NOT product_type:Other NOT product_type:Underfloor';
const RADIATOR_BRANDS = ['biasi', 'centerbrand', 'centerrad', 'centerrail', 'myson', 'nabis', 'purmo', 'reina', 'stelrad',
  'the radiator company', 'tradefix', 'wallace and squires', 'zehnder'];
const RADIATOR_WORDS = new Set((
  'radiator radiators rad rads towel rail rails warmer warmers heated column columns panel panels designer vertical '
  + 'horizontal electric lst convector convectors traditional cast iron aluminium ladder flat oval square compact plan '
  + 'slimline double single triple mini spa bathroom kitchen dual fuel straight curved low surface temperature '
  + 'lo-line hi-line k1 k2 k3 p1 p2 p plus type '
  + 'white black anthracite chrome grey gray silver gold brass copper nickel brushed satin matt matte gloss polished '
  + 'cream stainless steel bronze red blue green pink yellow ral').split(' '));
const NEUTRAL_WORDS = new Set('and with for in a of x'.split(' '));
const BRAND_WORDS = new Set(RADIATOR_BRANDS.flatMap((b) => b.split(' ')));

/** Append RAD_PARTS_SUFFIX to a generic radiator query as sent (search/normaliser.py radiators_first). */
export function radiatorsFirst(sent) {
  if (!sent || sent.includes(' OR ') || sent.includes('tag:"range:') || sent.includes('product_type:')) return sent;
  let signal = false;
  for (const t of tokens(sent)) {
    if (t.startsWith('vendor:"')) {
      if (!RADIATOR_BRANDS.includes(t.slice('vendor:"'.length, -1))) return sent;
      signal = true;
    } else if (t.startsWith('tag:"size:')) {
      signal = true;
    } else if (t.startsWith('tag:')) {
      continue; // BTU band, watts, orientation: neutral
    } else if (RADIATOR_WORDS.has(t) || BRAND_WORDS.has(t)) {
      signal = true;
    } else if (NEUTRAL_WORDS.has(t) || /^\d{2,4}(?:mm)?$/u.test(t)) {
      continue;
    } else {
      return sent; // an unknown word: leave the query alone
    }
  }
  return signal ? sent + RAD_PARTS_SUFFIX : sent;
}

/** The query without the radiators-first suffix (the "all results" form). */
export function withoutPartsSuffix(sent) {
  const s = String(sent || '');
  return s.endsWith(RAD_PARTS_SUFFIX) ? s.slice(0, -RAD_PARTS_SUFFIX.length) : s;
}

/** What the theme sends to Shopify (search/normaliser.py theme_query). */
export function themeQuery(q) {
  const raw = String(q || '');
  // field syntax already present (a sent query shown again): pass through, so normalising is idempotent (Astra TL-03);
  // 2026-11-02 also product_type: (a radiators-first query)
  if (/(?:tag|vendor):"|product_type:/u.test(raw)) return raw.split(' ').filter(Boolean).join(' ').trim();
  return radiatorsFirst(modelSyntax(tagSyntax(normaliseQuery(q))));
}

/** 2026-11-02 (visual QC r2 N4): [data-rad-ps-label] holds the label with a [[rad-terms]] marker for the shopper's words. */
export function showShopperWords(root, raw) {
  const words = String(raw || '').trim();
  if (!words) return;
  for (const el of root.querySelectorAll('[data-rad-ps-label]')) {
    const label = el.getAttribute('data-rad-ps-label') || '';
    if (label.includes('[[rad-terms]]')) el.textContent = label.replace('[[rad-terms]]', words);
  }
}

const FIELDS = 'title,product_type,vendor,tag,variants.sku,variants.barcode,variants.title';

function suggestUrl(root, q, types) {
  const p = new URLSearchParams({
    q,
    'resources[type]': types,
    'resources[limit]': '6',
    'resources[limit_scope]': 'each',
    'resources[options][fields]': FIELDS,
    'resources[options][prefix]': 'last',
    section_id: 'rad-predictive-search',
  });
  return `${root}search/suggest?${p}`;
}

async function fetchPart(url, signal) {
  const res = await fetch(url, { signal, headers: { Accept: 'text/html' } });
  if (!res.ok) throw new Error(String(res.status));
  const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
  return doc.querySelector('[data-rad-ps]');
}

function enhance(form) {
  const input = form.querySelector('input[name="q"]');
  const panel = form.querySelector('[data-rad-ps-panel]');
  const status = form.querySelector('[data-rad-ps-status]');
  if (!input || !panel) return;
  const root = form.dataset.root || '/';
  let timer = 0;
  let controller = null;
  let lastQuery = '';

  const links = () => [...panel.querySelectorAll('a[href]')];
  const close = () => {
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  };
  const open = () => {
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  async function run(raw) {
    const q = themeQuery(raw);
    if (q.length < 2) {
      close();
      return;
    }
    if (q === lastQuery && !panel.hidden) return;
    lastQuery = q;
    controller?.abort();
    controller = new AbortController();
    const { signal } = controller;
    try {
      const rawQ = raw.trim();
      const products = fetchPart(suggestUrl(root, q, rawQ === q ? 'product,collection,query' : 'product'), signal);
      const others = rawQ === q ? null : fetchPart(suggestUrl(root, rawQ, 'collection,query'), signal);
      let [p, o] = await Promise.all([products, others]);
      if (!p) return;
      const base = withoutPartsSuffix(q);
      if (base !== q && !p.querySelector('.rad-ps__group[data-part="products"] .rad-ps-item__link')) {
        // radiators first found nothing: show what the query finds with parts (never an empty panel because of the suffix)
        const all = await fetchPart(suggestUrl(root, base, rawQ === q ? 'product,collection,query' : 'product'), signal);
        if (all) p = all;
      }
      if (o) {
        for (const part of ['queries', 'collections']) {
          const mine = p.querySelector(`[data-part="${part}"]`);
          const theirs = o.querySelector(`[data-part="${part}"]`);
          if (theirs && mine) mine.replaceWith(theirs);
          else if (theirs) p.querySelector('[data-part="products"]')?.before(theirs);
        }
      }
      showShopperWords(p, rawQ);
      panel.replaceChildren(...p.childNodes);
      open();
      if (status) {
        const n = panel.querySelectorAll('[data-rad-ps-item]').length;
        status.textContent = n ? `${n} suggestions. Use the down arrow to browse.` : 'No suggestions.';
      }
    } catch (err) {
      if (err?.name !== 'AbortError') close();
    }
  }

  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = window.setTimeout(() => run(input.value), 180);
  });
  input.addEventListener('focus', () => {
    if (panel.childElementCount && input.value.trim().length > 1) open();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' && !panel.hidden) {
      e.preventDefault();
      links()[0]?.focus();
    } else if (e.key === 'Escape') {
      close();
    }
  });
  panel.addEventListener('keydown', (e) => {
    const all = links();
    const i = all.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      all[Math.min(i + 1, all.length - 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (i <= 0) input.focus();
      else all[i - 1].focus();
    } else if (e.key === 'Escape') {
      close();
      input.focus();
    }
  });
  document.addEventListener('click', (e) => {
    if (!form.contains(/** @type {Node} */ (e.target))) close();
  });
  form.addEventListener('focusout', (e) => {
    if (!form.contains(/** @type {Node} */ (e.relatedTarget))) close();
  });
  panel.addEventListener('click', (e) => {
    const a = /** @type {Element} */ (e.target).closest?.('a[href]');
    if (!a) return;
    const sent = new URL(a.href, window.location.href).searchParams.get('q');
    if (sent && a.pathname.endsWith('/search')) rememberShown(sent, input.value);
  });
  submitNormalised(form, input);
}

// ---- the shopper's own words in the search box (2026-10-26) ----
// The theme sends the tag syntax ("tag:"range:stelrad elite""), so the search page would echo it in its inputs and
// title. The words the shopper typed are kept in sessionStorage (this tab only, never sent) beside the query sent, and
// put back wherever the page shows exactly that sent query.
const SHOWN_KEY = 'rad-search-shown';

function rememberShown(sent, raw) {
  try {
    if (sent && raw && sent !== raw) window.sessionStorage.setItem(SHOWN_KEY, JSON.stringify({ sent, raw: raw.trim() }));
    else window.sessionStorage.removeItem(SHOWN_KEY);
  } catch {
    // storage blocked: the page then shows the query as sent
  }
}

/** On submit the visible field keeps the shopper's words; a hidden field named q carries the normalised query. */
function submitNormalised(form, input) {
  let hidden = null;
  form.addEventListener('submit', () => {
    const raw = input.value;
    const q = themeQuery(raw);
    if (!q || q === raw) {
      rememberShown('', '');
      return;
    }
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      form.append(hidden);
    }
    hidden.name = 'q';
    hidden.value = q;
    input.removeAttribute('name');
    rememberShown(q, raw);
  });
  // back/forward cache: the restored page must submit from the visible field again
  window.addEventListener('pageshow', () => {
    input.name = 'q';
    hidden?.remove();
    hidden = null;
  });
}

function readShown() {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(SHOWN_KEY) || 'null');
    if (saved && typeof saved.sent === 'string' && typeof saved.raw === 'string' && saved.raw) return saved;
  } catch {
    // storage blocked
  }
  return null;
}

/** Radiators first on the search page: "See all results" keeps the shopper's words; no results drops the suffix. */
function radiatorsFirstPage() {
  const url = new URL(window.location.href);
  const here = url.searchParams.get('q') || '';
  if (!here.endsWith(RAD_PARTS_SUFFIX)) return;
  const saved = readShown();
  const raw = saved && saved.sent === here ? saved.raw : '';
  for (const a of document.querySelectorAll('a[data-rad-search-all]')) {
    a.addEventListener('click', () => {
      const sent = new URL(a.href, window.location.href).searchParams.get('q');
      if (raw && sent) rememberShown(sent, raw);
    });
  }
  if (document.querySelector('.search-results__no-results')) {
    const base = withoutPartsSuffix(here);
    if (raw) rememberShown(base, raw);
    url.searchParams.set('q', base);
    window.location.replace(url.toString());
  }
}

function restoreShown() {
  const saved = readShown();
  if (!saved) return;
  const here = new URL(window.location.href).searchParams.get('q');
  if (here !== saved.sent) return;
  // 2026-11-03 R3-2: the page prints snippets/rad-search-shown-terms.liquid; put back the exact words typed
  const shown = new Set([saved.sent]);
  for (const el of document.querySelectorAll('input[name="q"]')) {
    if (!(el instanceof HTMLInputElement)) continue;
    if (el.defaultValue) shown.add(el.defaultValue);
    if (el.value === saved.sent || (el.defaultValue && el.value === el.defaultValue)) el.value = saved.raw;
  }
  for (const s of shown) {
    const quoted = `“${s}”`; // quoted, so a count is never replaced
    if (document.title.includes(quoted)) {
      document.title = document.title.replace(quoted, `“${saved.raw}”`);
      break;
    }
  }
  for (const p of document.querySelectorAll('.search-results__no-results p')) {
    for (const s of shown) {
      if (p.textContent?.includes(`"${s}"`)) {
        p.textContent = p.textContent.replace(`"${s}"`, `"${saved.raw}"`);
        break;
      }
    }
  }
}

if (typeof document !== 'undefined') {
  const init = () => {
    document.querySelectorAll('form[data-rad-search]:not([data-rad-ready])').forEach((f) => {
      f.setAttribute('data-rad-ready', '');
      enhance(f);
    });
    // the search page's own field (blocks/_search-input.liquid) goes through the same normaliser on submit
    document.querySelectorAll('form.search-page-input__parent:not([data-rad-ready])').forEach((f) => {
      const input = f.querySelector('input[name="q"]');
      if (!(input instanceof HTMLInputElement)) return;
      f.setAttribute('data-rad-ready', '');
      submitNormalised(f, input);
    });
    restoreShown();
    radiatorsFirstPage();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
}
